import * as analytics from 'analytics/Analytics';
import {assert} from 'Asserts';
import {MOLBIO_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {Strand} from 'constants/MolbioConstants';
import {MatchSite} from 'constants/SequenceConstants';
import {
  CodonOptimizationMethod,
  CodonOptimizationPreferences,
  CodonOptimizationSelectionTab,
  GCContent,
  GCLevel,
  HairpinParameter,
  OptimizeCodonsResult,
  Organism,
} from 'molbio/codon-optimization/CodonOptimizationConstants';
import {
  getApiPatternsToReduce,
  getCutsitesToAvoidForSequence,
  getProtectedRegionsForSequence,
  getRegionBounds,
  getUniqueProtectedRegions,
} from 'molbio/codon-optimization/CodonOptimizationUtil';
import {
  EnzymeRecognitionSite,
  PatternToReduce,
  ProtectedRegion,
} from 'molbio/codon-optimization/reduceCodonOptimizationTab';
import {APIEnzymeRecognitionSite, optimizeCodons} from 'molbio/dao/CodonOptimizationDAO';
import preferences from 'Preferences';
import {Thunk} from 'redux-stores/Store';
import {NumericRange} from 'util/geometry/GeometryUtil';

export enum CodonOptimizationTabActionType {
  NEW_TAB = 'CODON_OPTIMIZATION_TAB_NEW_TAB',
  UPDATE_SELECTED_ORGANISM = 'CODON_OPTIMIZATION_TAB_UPDATE_SELECTED_ORGANISM',
  UPDATE_SELECTED_GC_LEVEL = 'CODON_OPTIMIZATION_TAB_UPDATE_SELECTED_GC_LEVEL',
  PREVIEW_PENDING = 'CODON_OPTIMIZATION_TAB_PREVIEW_PENDING',
  PREVIEW_COMPLETED = 'CODON_OPTIMIZATION_TAB_PREVIEW_COMPLETED',
  PREVIEW_ERROR = 'CODON_OPTIMIZATION_TAB_PREVIEW_ERROR',
  SAVE_OPTIMIZATION_COMPLETED = 'CODON_OPTIMIZATION_TAB_SAVE_OPTIMIZATION_COMPLETED',
  CLOSE_OPTIMIZATION = 'CODON_OPTIMIZATION_TAB_CLOSE_OPTIMIZATION',
  GO_BACK = 'CODON_OPTIMIZATION_TAB_GO_BACK',
  SET_SELECTION = 'CODON_OPTIMIZATION_TAB_SET_SELECTION',
  ADD_PROTECTED_REGION = 'CODON_OPTIMIZATION_TAB_ADD_PROTECTED_REGION',
  DELETE_ALL_PROTECTED_REGIONS = 'DELETE_ALL_PROTECTED_REGIONS',
  DELETE_PROTECTED_REGION = 'CODON_OPTIMIZATION_TAB_DELETE_PROTECTED_REGION',
  IS_INVALID_PROTECTED_REGION = 'CODON_OPTIMIZATION_TAB_IS_INVALID_PROTECTED_REGION',
  ADD_CUTSITE_TO_AVOID = 'CODON_OPTIMIZATION_TAB_ADD_CUTSITE_TO_AVOID',
  DELETE_ALL_CUTSITES_TO_AVOID = 'DELETE_ALL_CUTSITES_TO_AVOID',
  DELETE_CUTSITE_TO_AVOID = 'CODON_OPTIMIZATION_TAB_DELETE_CUTSITE_TO_AVOID',
  ADD_CUTSITE_TO_PRESERVE = 'CODON_OPTIMIZATION_TAB_ADD_CUTSITE_TO_PRESERVE',
  DELETE_ALL_CUTSITES_TO_PRESERVE = 'DELETE_ALL_CUTSITES_TO_PRESERVE',
  DELETE_CUTSITE_TO_PRESERVE = 'CODON_OPTIMIZATION_TAB_DELETE_CUTSITE_TO_PRESERVE',
  UPDATE_URIDINE_DEPLETION = 'CODON_OPTIMIZATION_TAB_UPDATE_URIDINE_DEPLETION',
  UPDATE_HAIRPIN_PARAMETERS = 'CODON_OPTIMIZATION_TAB_UPDATE_HAIRPIN_PARAMETERS',
  UPDATE_ACTIVE_TAB = 'CODON_OPTIMIZATION_TAB_UPDATE_ACTIVE_TAB',
  ADD_PATTERN_TO_REDUCE = 'CODON_OPTIMIZATION_ADD_PATTERN_TO_REDUCE',
  DELETE_ALL_PATTERNS_TO_REDUCE = 'CODON_OPTIMIZATION_DELETE_ALL_PATTERNS_TO_REDUCE',
  DELETE_PATTERN_TO_REDUCE = 'CODON_OPTIMIZATION_DELETE_PATTERN_TO_REDUCE',
  UPDATE_PATTERN_TO_REDUCE = 'CODON_OPTIMIZATION_UPDATE_PATTERN_TO_REDUCE',
  UPDATE_CUSTOM_GC_CONTENT = 'UPDATE_CUSTOM_GC_CONTENT',
  UPDATE_OPTIMIZATION_METHOD = 'UPDATE_OPTIMIZATION_METHOD',
}

export interface CodonOptimizationTabNewTabAction {
  type: CodonOptimizationTabActionType.NEW_TAB;
  tabId: string;
}
export function newTab(tabId: string): CodonOptimizationTabNewTabAction {
  return {
    type: CodonOptimizationTabActionType.NEW_TAB,
    tabId,
  };
}

export interface CodonOptimizationTabOrganismAction {
  type: CodonOptimizationTabActionType.UPDATE_SELECTED_ORGANISM;
  selectedOrganism: Organism;
  tabId: string;
}
export function updateOrganism(organism: Organism, tabId: string): CodonOptimizationTabOrganismAction {
  return {
    type: CodonOptimizationTabActionType.UPDATE_SELECTED_ORGANISM,
    selectedOrganism: organism,
    tabId,
  };
}

export interface CodonOptimizationTabGCAction {
  type: CodonOptimizationTabActionType.UPDATE_SELECTED_GC_LEVEL;
  selectedGCLevel: GCLevel;
  tabId: string;
}
export function updateGC(level: GCLevel, tabId: string): CodonOptimizationTabGCAction {
  return {
    type: CodonOptimizationTabActionType.UPDATE_SELECTED_GC_LEVEL,
    selectedGCLevel: level,
    tabId,
  };
}

interface CodonOptimizationTabPreviewPendingAction {
  type: CodonOptimizationTabActionType.PREVIEW_PENDING;
  tabId: string;
}
export function previewPending(tabId: string): CodonOptimizationTabPreviewPendingAction {
  return {
    type: CodonOptimizationTabActionType.PREVIEW_PENDING,
    tabId,
  };
}
interface CodonOptimizationTabPreviewCompletedAction {
  type: CodonOptimizationTabActionType.PREVIEW_COMPLETED;
  result: OptimizeCodonsResult;
  tabId: string;
}
export function previewCompleted(
  result: OptimizeCodonsResult,
  tabId: string
): CodonOptimizationTabPreviewCompletedAction {
  return {
    type: CodonOptimizationTabActionType.PREVIEW_COMPLETED,
    result,
    tabId,
  };
}
interface CodonOptimizationTabPreviewErrorAction {
  type: CodonOptimizationTabActionType.PREVIEW_ERROR;
  error: string;
  tabId: string;
}
export function previewError(error: string, tabId: string): CodonOptimizationTabPreviewErrorAction {
  return {
    type: CodonOptimizationTabActionType.PREVIEW_ERROR,
    error,
    tabId,
  };
}

export function onPreview(
  tabId: string,
  sequenceId: string,
  isBackTranslation: boolean,
  matchSites: Array<MatchSite> | null,
  cutsitesToPreserve: Array<EnzymeRecognitionSite>,
  cutsitesToAvoid: Array<EnzymeRecognitionSite>,
  optimizationStart: number,
  optimizationEnd: number,
  seqLength: number,
  strand: Strand,
  protectedRegions: Array<ProtectedRegion>,
  bases: string,
  selectedOrganism: Organism | null,
  selectedGCLevel: GCLevel,
  uridineDepletion: boolean,
  avoidHairpins: boolean,
  hairpinStem: HairpinParameter,
  hairpinWindow: HairpinParameter,
  patternsToReduce: Array<PatternToReduce>,
  method: CodonOptimizationMethod,
  gcContent: GCContent
): Thunk<void> {
  return async (dispatch) => {
    dispatch(previewPending(tabId));
    let regionsToProtect: Array<NumericRange> = [];
    let apiCutsitesToAvoid: Array<APIEnzymeRecognitionSite> = [];
    preferences.set(CodonOptimizationPreferences.REDUCE_PATTERNS, patternsToReduce);
    if (!isBackTranslation) {
      // eslint-disable-next-line benchlint/no-eqeqeq-null
      assert(matchSites !== null, 'Expected matchSites to be set for codon optimizing a DNA sequence.');
      regionsToProtect = getProtectedRegionsForSequence(
        matchSites,
        cutsitesToPreserve,
        optimizationStart,
        optimizationEnd,
        seqLength,
        strand,
        protectedRegions
      );
      apiCutsitesToAvoid = getCutsitesToAvoidForSequence(
        cutsitesToPreserve,
        cutsitesToAvoid,
        matchSites,
        optimizationStart,
        optimizationEnd,
        seqLength,
        strand
      );
    } else {
      regionsToProtect = getUniqueProtectedRegions(
        protectedRegions.flatMap((region) =>
          getRegionBounds(region.start, region.end, optimizationStart, optimizationEnd, seqLength, strand)
        )
      );
      apiCutsitesToAvoid.push(
        ...cutsitesToAvoid.map(
          (cutsite): APIEnzymeRecognitionSite => ({
            recognitionSiteDna: cutsite.recognitionSite,
            start: 0,
            end: bases.length,
          })
        )
      );
    }
    const apiPatterns = getApiPatternsToReduce(patternsToReduce);
    const analyticsMetadata = {
      numBases: bases.length,
      organism: selectedOrganism,
      gcLevel: selectedGCLevel,
      numProtectedRegions: regionsToProtect.length,
      numCutsitesToAvoid: apiCutsitesToAvoid.length,
      uridineDepletion,
      avoidHairpins,
      hairpinStem,
      hairpinWindow,
      numReducedPatterns: apiPatterns.length,
      method,
      gcContent,
    };
    try {
      const result = await optimizeCodons(
        bases,
        selectedOrganism,
        selectedGCLevel,
        regionsToProtect,
        apiCutsitesToAvoid,
        uridineDepletion, // mRNA uridine depletion is equivalent to minimizing thymine in DNA
        avoidHairpins,
        hairpinStem,
        hairpinWindow,
        apiPatterns,
        method,
        gcContent
      );
      dispatch(previewCompleted(result, tabId));
      analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.PERFORMED_CODON_OPTIMIZATION, {
        ...analyticsMetadata,
        type: 'success',
        polymerId: sequenceId,
      });
    } catch (err) {
      analytics.trackEvent(MOLBIO_ANALYTICS_ACTIONS.PERFORMED_CODON_OPTIMIZATION, {
        ...analyticsMetadata,
        type: 'failure',
        polymerId: sequenceId,
      });
      if (!err.responseJSON || !err.responseJSON.userMessage) {
        dispatch(previewError('', tabId));
        throw err;
      } else {
        dispatch(previewError(err.responseJSON.userMessage, tabId));
      }
    }
  };
}

export interface CodonOptimizationTabSaveOptimizationCompletedAction {
  type: CodonOptimizationTabActionType.SAVE_OPTIMIZATION_COMPLETED;
  tabId: string;
}
export function saveOptimization(tabId: string): CodonOptimizationTabSaveOptimizationCompletedAction {
  return {
    type: CodonOptimizationTabActionType.SAVE_OPTIMIZATION_COMPLETED,
    tabId,
  };
}

export interface CodonOptimizationTabCloseOptimizationAction {
  type: CodonOptimizationTabActionType.CLOSE_OPTIMIZATION;
  tabId: string;
}
export function closeOptimization(tabId: string): CodonOptimizationTabCloseOptimizationAction {
  return {
    type: CodonOptimizationTabActionType.CLOSE_OPTIMIZATION,
    tabId,
  };
}

export interface CodonOptimizationTabBackAction {
  type: CodonOptimizationTabActionType.GO_BACK;
  tabId: string;
}
export function goBack(tabId: string): CodonOptimizationTabBackAction {
  return {
    type: CodonOptimizationTabActionType.GO_BACK,
    tabId,
  };
}

export interface CodonOptimizationTabSetSelectionAction {
  type: CodonOptimizationTabActionType.SET_SELECTION;
  tabId: string;
  start: number;
  end: number;
  bases: string;
  isOptimizationEntireSequence: boolean;
  strand: Strand; // 1 for forward, -1 for reverse, 0 for protein (protein is functionally forward)
}
export function setSelection(
  tabId: string,
  start: number,
  end: number,
  bases: string,
  isOptimizationEntireSequence: boolean,
  strand: Strand
): CodonOptimizationTabSetSelectionAction {
  return {
    type: CodonOptimizationTabActionType.SET_SELECTION,
    tabId,
    start,
    end,
    bases,
    isOptimizationEntireSequence,
    strand,
  };
}

export interface CodonOptimizationTabAddProtectedRegionAction {
  type: CodonOptimizationTabActionType.ADD_PROTECTED_REGION;
  tabId: string;
  region: ProtectedRegion;
}
export function addProtectedRegion(
  tabId: string,
  region: ProtectedRegion
): CodonOptimizationTabAddProtectedRegionAction {
  return {
    type: CodonOptimizationTabActionType.ADD_PROTECTED_REGION,
    tabId,
    region,
  };
}

export interface CodonOptimizationTabDeleteProtectedRegionAction {
  type: CodonOptimizationTabActionType.DELETE_PROTECTED_REGION;
  tabId: string;
  region: ProtectedRegion;
}
export function deleteProtectedRegion(
  tabId: string,
  region: ProtectedRegion
): CodonOptimizationTabDeleteProtectedRegionAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_PROTECTED_REGION,
    tabId,
    region,
  };
}

export interface CodonOptimizationTabDeleteAllProtectedRegionsAction {
  type: CodonOptimizationTabActionType.DELETE_ALL_PROTECTED_REGIONS;
  tabId: string;
}
export function deleteAllProtectedRegions(
  tabId: string
): CodonOptimizationTabDeleteAllProtectedRegionsAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_ALL_PROTECTED_REGIONS,
    tabId,
  };
}

export interface CodonOptimizationTabIsInvalidProtectedRegionAction {
  type: CodonOptimizationTabActionType.IS_INVALID_PROTECTED_REGION;
  tabId: string;
  message: string;
}
export function isInvalidProtectedRegion(
  tabId: string,
  message: string
): CodonOptimizationTabIsInvalidProtectedRegionAction {
  return {
    type: CodonOptimizationTabActionType.IS_INVALID_PROTECTED_REGION,
    tabId,
    message,
  };
}

export interface CodonOptimizationTabAddCutsiteToAvoidAction {
  type: CodonOptimizationTabActionType.ADD_CUTSITE_TO_AVOID;
  tabId: string;
  cutsite: EnzymeRecognitionSite;
}
export function addCutsiteToAvoid(
  tabId: string,
  cutsite: EnzymeRecognitionSite
): CodonOptimizationTabAddCutsiteToAvoidAction {
  return {
    type: CodonOptimizationTabActionType.ADD_CUTSITE_TO_AVOID,
    tabId,
    cutsite,
  };
}

export interface CodonOptimizationTabDeleteCutsiteToAvoidAction {
  type: CodonOptimizationTabActionType.DELETE_CUTSITE_TO_AVOID;
  tabId: string;
  enzymeId: number;
}
export function deleteCutsiteToAvoid(
  tabId: string,
  enzymeId: number
): CodonOptimizationTabDeleteCutsiteToAvoidAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_CUTSITE_TO_AVOID,
    tabId,
    enzymeId,
  };
}

export interface CodonOptimizationTabDeleteAllCutsitesToAvoidAction {
  type: CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_AVOID;
  tabId: string;
}
export function deleteAllCutsitesToAvoid(tabId: string): CodonOptimizationTabDeleteAllCutsitesToAvoidAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_AVOID,
    tabId,
  };
}

export interface CodonOptimizationTabAddCutsiteToPreserveAction {
  type: CodonOptimizationTabActionType.ADD_CUTSITE_TO_PRESERVE;
  tabId: string;
  cutsite: EnzymeRecognitionSite;
}
export function addCutsiteToPreserve(
  tabId: string,
  cutsite: EnzymeRecognitionSite
): CodonOptimizationTabAddCutsiteToPreserveAction {
  return {
    type: CodonOptimizationTabActionType.ADD_CUTSITE_TO_PRESERVE,
    tabId,
    cutsite,
  };
}

export interface CodonOptimizationTabDeleteCutsiteToPreserveAction {
  type: CodonOptimizationTabActionType.DELETE_CUTSITE_TO_PRESERVE;
  tabId: string;
  enzymeId: number;
}
export function deleteCutsiteToPreserve(
  tabId: string,
  enzymeId: number
): CodonOptimizationTabDeleteCutsiteToPreserveAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_CUTSITE_TO_PRESERVE,
    tabId,
    enzymeId,
  };
}

export interface CodonOptimizationTabDeleteAllCutsitesToPreserveAction {
  type: CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_PRESERVE;
  tabId: string;
}
export function deleteAllCutsitesToPreserve(
  tabId: string
): CodonOptimizationTabDeleteAllCutsitesToPreserveAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_ALL_CUTSITES_TO_PRESERVE,
    tabId,
  };
}

export interface CodonOptimizationTabUpdateUridineDepletionAction {
  type: CodonOptimizationTabActionType.UPDATE_URIDINE_DEPLETION;
  tabId: string;
  uridineDepletion: boolean;
}
export function updateUridineDepletion(
  tabId: string,
  uridineDepletion: boolean
): CodonOptimizationTabUpdateUridineDepletionAction {
  return {
    type: CodonOptimizationTabActionType.UPDATE_URIDINE_DEPLETION,
    tabId,
    uridineDepletion,
  };
}

export interface CodonOptimizationTabUpdateHairpinParametersAction {
  type: CodonOptimizationTabActionType.UPDATE_HAIRPIN_PARAMETERS;
  tabId: string;
  avoidHairpins: boolean;
  hairpinStem: HairpinParameter;
  hairpinWindow: HairpinParameter;
}
export function updateHairpinParameters(
  tabId: string,
  avoidHairpins: boolean,
  hairpinStemValue: number,
  hairpinWindowValue: number,
  basesLength: number
): CodonOptimizationTabUpdateHairpinParametersAction {
  let hairpinStemError = '';
  let hairpinWindowError = '';
  if (avoidHairpins) {
    if (!Number.isInteger(hairpinStemValue)) {
      hairpinStemError = 'Stem size must be an integer';
    }
    if (!Number.isInteger(hairpinWindowValue)) {
      hairpinWindowError = 'Window size must be an integer';
    }
    if (hairpinStemValue === 0) {
      hairpinStemError = 'Enter a nonzero stem size';
    }
    if (hairpinWindowValue === 0) {
      hairpinWindowError = 'Enter a nonzero window size';
    }
    if (hairpinStemValue < 0) {
      hairpinStemError = 'Stem size must be positive';
    }
    if (hairpinWindowValue < 0) {
      hairpinWindowError = 'Window size must be positive';
    }
    if (hairpinStemValue > hairpinWindowValue / 2) {
      hairpinStemError = 'Stem size is greater than half the window size';
    }
    if (hairpinWindowValue > basesLength) {
      hairpinWindowError = 'Window size is larger than selected region';
    }
  }
  const hairpinStem = {value: hairpinStemValue, error: hairpinStemError};
  const hairpinWindow = {value: hairpinWindowValue, error: hairpinWindowError};
  preferences.set(CodonOptimizationPreferences.AVOID_HAIRPINS, avoidHairpins);
  preferences.set(CodonOptimizationPreferences.HAIRPIN_STEM, hairpinStem);
  preferences.set(CodonOptimizationPreferences.HAIRPIN_WINDOW, hairpinWindow);
  return {
    type: CodonOptimizationTabActionType.UPDATE_HAIRPIN_PARAMETERS,
    tabId,
    avoidHairpins,
    hairpinStem,
    hairpinWindow,
  };
}

export interface CodonOptimizationTabUpdateActiveTabAction {
  type: CodonOptimizationTabActionType.UPDATE_ACTIVE_TAB;
  tabId: string;
  tab: CodonOptimizationSelectionTab;
}
export function updateActiveTab(
  tabId: string,
  tab: CodonOptimizationSelectionTab
): CodonOptimizationTabUpdateActiveTabAction {
  return {
    type: CodonOptimizationTabActionType.UPDATE_ACTIVE_TAB,
    tabId,
    tab,
  };
}

export interface CodonOptimizationTabAddPatternToReduceAction {
  type: CodonOptimizationTabActionType.ADD_PATTERN_TO_REDUCE;
  tabId: string;
}
export function addPatternToReduce(tabId: string): CodonOptimizationTabAddPatternToReduceAction {
  return {
    type: CodonOptimizationTabActionType.ADD_PATTERN_TO_REDUCE,
    tabId,
  };
}

export interface CodonOptimizationTabDeleteAllPatternsToReduceAction {
  type: CodonOptimizationTabActionType.DELETE_ALL_PATTERNS_TO_REDUCE;
  tabId: string;
}
export function deleteAllPatternsToReduce(
  tabId: string
): CodonOptimizationTabDeleteAllPatternsToReduceAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_ALL_PATTERNS_TO_REDUCE,
    tabId,
  };
}

export interface CodonOptimizationTabDeletePatternToReduceAction {
  type: CodonOptimizationTabActionType.DELETE_PATTERN_TO_REDUCE;
  tabId: string;
  patternIdx: number;
}
export function deletePatternToReduce(
  tabId: string,
  patternIdx: number
): CodonOptimizationTabDeletePatternToReduceAction {
  return {
    type: CodonOptimizationTabActionType.DELETE_PATTERN_TO_REDUCE,
    tabId,
    patternIdx,
  };
}

export interface CodonOptimizationTabUpdatePatternToReduceAction {
  type: CodonOptimizationTabActionType.UPDATE_PATTERN_TO_REDUCE;
  tabId: string;
  patternIdx: number;
  patternToReduce: PatternToReduce;
}
export function updatePatternToReduce(
  tabId: string,
  patternIdx: number,
  patternToReduce: PatternToReduce
): CodonOptimizationTabUpdatePatternToReduceAction {
  return {
    type: CodonOptimizationTabActionType.UPDATE_PATTERN_TO_REDUCE,
    tabId,
    patternIdx,
    patternToReduce,
  };
}

export interface CodonOptimizationTabCustomGCCOntentAction {
  type: CodonOptimizationTabActionType.UPDATE_CUSTOM_GC_CONTENT;
  gcContent: GCContent;
  tabId: string;
}
export function updateGCContent(
  gcContent: GCContent,
  tabId: string
): CodonOptimizationTabCustomGCCOntentAction {
  preferences.set(CodonOptimizationPreferences.GC_CONTENT, gcContent);
  return {
    type: CodonOptimizationTabActionType.UPDATE_CUSTOM_GC_CONTENT,
    gcContent,
    tabId,
  };
}

export interface CodonOptimizationTabOptimizationMethodAction {
  type: CodonOptimizationTabActionType.UPDATE_OPTIMIZATION_METHOD;
  method: CodonOptimizationMethod;
  tabId: string;
}
export function updateMethod(
  method: CodonOptimizationMethod,
  tabId: string
): CodonOptimizationTabOptimizationMethodAction {
  preferences.set(CodonOptimizationPreferences.METHOD, method);
  return {
    type: CodonOptimizationTabActionType.UPDATE_OPTIMIZATION_METHOD,
    method,
    tabId,
  };
}

export type CodonOptimizationTabAction =
  | CodonOptimizationTabNewTabAction
  | CodonOptimizationTabOrganismAction
  | CodonOptimizationTabGCAction
  | CodonOptimizationTabPreviewPendingAction
  | CodonOptimizationTabPreviewCompletedAction
  | CodonOptimizationTabPreviewErrorAction
  | CodonOptimizationTabSaveOptimizationCompletedAction
  | CodonOptimizationTabCloseOptimizationAction
  | CodonOptimizationTabBackAction
  | CodonOptimizationTabSetSelectionAction
  | CodonOptimizationTabAddProtectedRegionAction
  | CodonOptimizationTabDeleteProtectedRegionAction
  | CodonOptimizationTabIsInvalidProtectedRegionAction
  | CodonOptimizationTabAddCutsiteToAvoidAction
  | CodonOptimizationTabDeleteCutsiteToAvoidAction
  | CodonOptimizationTabAddCutsiteToPreserveAction
  | CodonOptimizationTabDeleteCutsiteToPreserveAction
  | CodonOptimizationTabUpdateUridineDepletionAction
  | CodonOptimizationTabUpdateHairpinParametersAction
  | CodonOptimizationTabUpdateActiveTabAction
  | CodonOptimizationTabAddPatternToReduceAction
  | CodonOptimizationTabDeletePatternToReduceAction
  | CodonOptimizationTabUpdatePatternToReduceAction
  | CodonOptimizationTabDeleteAllPatternsToReduceAction
  | CodonOptimizationTabDeleteAllCutsitesToAvoidAction
  | CodonOptimizationTabDeleteAllCutsitesToPreserveAction
  | CodonOptimizationTabDeleteAllProtectedRegionsAction
  | CodonOptimizationTabCustomGCCOntentAction
  | CodonOptimizationTabOptimizationMethodAction;
